import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { clearStudentSession } from '../utils/sessionStorage';
import { saveZidToCookie, clearZidCookie, clearCampusSlugCookie } from '../utils/campusFilter';

interface DecodedPayload {
  'cognito:username'?: unknown;
  preferred_username?: unknown;
  name?: unknown;
  given_name?: unknown;
  family_name?: unknown;
  phone_number?: unknown;
  email?: unknown;
  profile?: unknown;
  exp?: number;
}

const str = (v: unknown): string => (v != null ? String(v) : '');

export interface User {
  username: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  phone_number?: string;
  email?: string;
  profile?: string;
  preferred_username?: string;
}

export interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null; // Always null — Amplify manages refresh internally
  parentId: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isNewUser: boolean;
}

export interface AuthContextType extends AuthState {
  login: (accessToken: string, refreshToken: string, idToken: string) => void;
  logout: () => Promise<void>;
  setIsNewUser: (value: boolean) => void;
  checkAuth: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    accessToken: null,
    refreshToken: null,
    parentId: null,
    isAuthenticated: false,
    isLoading: true,
    isNewUser: false,
  });

  // Initialize auth state from Amplify session (tokens stored in CookieStorage)
  useEffect(() => {
    const initAuth = async () => {
      // Preserve zid query param in cookie before auth check
      // so it survives the login/signup redirect flow
      const urlParams = new URLSearchParams(window.location.search);
      const zid = urlParams.get('zid');
      if (zid) {
        saveZidToCookie(zid);
      }

      try {
        const session = await fetchAuthSession();
        if (session.tokens?.accessToken) {
          const payload = (session.tokens.idToken?.payload ?? {}) as DecodedPayload;
          const user: User = {
            username: str(payload['cognito:username']),
            name: `${str(payload.given_name)} ${str(payload.family_name)}`.trim() || undefined,
            given_name: str(payload.given_name) || undefined,
            family_name: str(payload.family_name) || undefined,
            phone_number: str(payload.phone_number) || undefined,
            email: str(payload.email) || undefined,
            profile: str(payload.profile) || undefined,
            preferred_username: str(payload.preferred_username) || undefined,
          };
          const parentId = str(payload.preferred_username) || str(payload['cognito:username']);
          
          // Check if user profile is PARENT
          if (user.profile !== 'PARENT') {
            console.warn('[AuthContext] User profile is not PARENT, logging out:', user.profile);
            // Sign out globally to clear tokens
            try {
              await signOut({ global: true });
            } catch (e) {
              console.error('[AuthContext] Error signing out non-PARENT user:', e);
            }
            setState(prev => ({ ...prev, isLoading: false }));
            return;
          }
          
          setState({
            user,
            accessToken: session.tokens.accessToken.toString(),
            refreshToken: null,
            parentId,
            isAuthenticated: true,
            isLoading: false,
            isNewUser: false,
          });
        } else {
          setState(prev => ({ ...prev, isLoading: false }));
        }
      } catch {
        // No active session
        setState(prev => ({ ...prev, isLoading: false }));
      }
    };

    initAuth();
  }, []);

  /**
   * Called by Login.tsx after verifyOtp() succeeds.
   * Tokens are already in Amplify CookieStorage — just update React state.
   */
  const login = useCallback((accessToken: string, _refreshToken: string, idToken: string) => {
    try {
      // Decode idToken manually (it's a JWT string returned by verifyOtp)
      const parts = idToken.split('.');
      if (parts.length !== 3) throw new Error('Invalid idToken');
      const payload: DecodedPayload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));

      const user: User = {
        username: str(payload['cognito:username']),
        name: `${str(payload.given_name)} ${str(payload.family_name)}`.trim() || undefined,
        given_name: str(payload.given_name) || undefined,
        family_name: str(payload.family_name) || undefined,
        phone_number: str(payload.phone_number) || undefined,
        email: str(payload.email) || undefined,
        profile: str(payload.profile) || undefined,
        preferred_username: str(payload.preferred_username) || undefined,
      };
      const parentId = str(payload.preferred_username) || str(payload['cognito:username']);

      // Check if user profile is PARENT
      if (user.profile !== 'PARENT') {
        console.warn('[AuthContext] User profile is not PARENT, rejecting login:', user.profile);
        setState(prev => ({ ...prev, isLoading: false }));
        return;
      }

      setState({
        user,
        accessToken,
        refreshToken: null,
        parentId,
        isAuthenticated: true,
        isLoading: false,
        isNewUser: false,
      });
    } catch (e) {
      console.error('Error during login:', e);
    }
  }, []);

  /**
   * Signs out globally (invalidates all tokens server-side) via Amplify,
   * clears React state and student session data, then redirects.
   */
  const logout = useCallback(async () => {
    try {
      await signOut({ global: true });
    } catch (e) {
      console.error('signOut error (proceeding anyway):', e);
    }

    // Clear all local state and session data before redirect
    clearStudentSession();
    clearZidCookie();
    clearCampusSlugCookie();
    localStorage.clear();
    sessionStorage.clear();

    // Clear all cookies
    document.cookie.split(';').forEach((c) => {
      const name = c.split('=')[0].trim();
      document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`;
    });

    setState({
      user: null,
      accessToken: null,
      refreshToken: null,
      parentId: null,
      isAuthenticated: false,
      isLoading: false,
      isNewUser: false,
    });

    window.location.href = '/';
  }, []);

  const setIsNewUser = useCallback((value: boolean) => {
    setState(prev => ({ ...prev, isNewUser: value }));
  }, []);

  const checkAuth = useCallback((): boolean => {
    return state.isAuthenticated;
  }, [state.isAuthenticated]);

  return (
    <AuthContext.Provider value={{ ...state, login, logout, setIsNewUser, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
